import type { Suit } from './identity.js';

export type MediaKind = 'photo' | 'video';

/** Video is capped by duration, checked client-side before a byte moves (D25). */
export const MAX_VIDEO_SECONDS = 180;

/** Hard backstop enforced by S3 on the presigned POST. Stops a pathological
 *  file; it is not meant to shape behaviour. */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

/** Anything at or above this uses multipart — a single PUT that dies at minute
 *  eleven restarts from zero (D25). In practice this is video only: a 2560px
 *  photo is about a megabyte. */
export const MULTIPART_THRESHOLD_BYTES = 8 * 1024 * 1024;

/** Long edge of the display copy — what detail view and the projector are
 *  served. The reception screen is 1080p, so this is a 1.33x downscale on the
 *  way to it: nothing visible is lost, at a quarter of the camera original's
 *  bytes. Made in the browser; never upscales a smaller source. */
export const DISPLAY_LONG_EDGE_PX = 2560;

/** Long edge of the grid thumbnail. Also made in the browser. */
export const THUMB_LONG_EDGE_PX = 480;

/**
 * Video is normalised to H.264/AAC with its **long edge** at this or below —
 * the codec matters more than the resolution, since iPhone HEVC plays
 * inconsistently in Chrome.
 *
 * Long edge, not height, because guests shoot both ways up. A height cap reads
 * as "1080p" and is right for landscape, but applied to a portrait 1080x1920
 * clip it means 608x1080 — throwing away almost half the detail of the most
 * common phone video there is. Capping the long edge gives exactly 1920x1080
 * for landscape and leaves portrait untouched, which is what "1080p" was
 * always meant to mean here.
 *
 * **Never upscale.** The processor scales down to this or leaves the source
 * alone; it does not scale up to reach it. See `media-processor`.
 */
export const VIDEO_MAX_LONG_EDGE_PX = 1920;

/**
 * Likes needed before a photo features itself (D52).
 *
 * **Deliberately low.** At the 3,000–10,000 photos this week is expected to
 * produce, nobody scrolls the whole gallery, so likes track *when* a photo was
 * uploaded far more than how good it is. A high bar would fill Featured on the
 * first evening and leave it silent for the rest of the week.
 *
 * It lives here rather than in a Lambda environment variable so that the server
 * enforcing it and the client saying "one more like" cannot disagree. An env
 * var moves the server's copy only, and a client still shipping the old number
 * tells a guest a photo is one like from the wall when it is two.
 *
 * `scripts/rebuild-featured.mjs` cannot import this file — it is `.mjs` and this
 * package ships TypeScript source — so it parses the number out of it and fails
 * loudly if it cannot. Keep the declaration on one line.
 */
export const FEATURE_THRESHOLD = 3;

/**
 * One row of the `photos` table as the API returns it.
 *
 * `uploaderName` and `uploaderTeam` are denormalised onto the record so the
 * shared gallery can attribute an image without a second read (D35).
 */
export interface MediaItem {
  id: string;
  kind: MediaKind;
  /**
   * The display copy — 2560px, sized against the 1080p reception screen (D25
   * amendment). Made in the browser at enqueue, so for a photo this is always
   * populated. For video it is the transcoded 1080p output and is empty until
   * MediaConvert finishes, during which the poster frame in `thumb` stands in.
   */
  src: string;
  /**
   * Grid thumbnail — 480px for photos, the poster frame for video. Made in the
   * browser and always present. The dominant bandwidth cost on the whole system.
   */
  thumb: string;
  durationSec?: number;
  uploaderId: string;
  uploaderName: string;
  uploaderTeam: Suit;
  caption?: string;
  eventId?: string;
  createdAt: number;
  /**
   * Reversible visibility flag. Admin-only, and never a delete (D40).
   *
   * **Being retired** (D50). It still works, and the gallery is still read from
   * its GSI2 partition, until the web app has moved over — otherwise hiding
   * would keep returning `ok` while quietly doing nothing, which is the worst
   * shape a removal control can have.
   */
  hidden: boolean;
  /**
   * On the wall, and in the Featured tab (D51). Set by three likes or by an
   * organiser, and it **latches** — withdrawing likes never clears it.
   *
   * The row also carries `feat`, a derived key that exists only while this is
   * true and is what makes the featured index sparse. It never reaches the
   * wire: see `store.ts`, which is the only place either is written.
   */
  featured: boolean;
  /** Denormalised, maintained by atomic `ADD`. Never counted on read — see
   *  `store.ts` on why `Select: 'COUNT'` is not the cheap option it looks. */
  likeCount: number;
  /** How it got there, for the UI and for the rebuild. Absent when not
   *  featured. `'upload'` is deliberately not a value: nothing is featured on
   *  arrival, including an admin's own uploads (D52). */
  featuredBy?: 'likes' | 'admin';
}

/** What the kiosk pulls: photos only, non-hidden, IDs and URLs rather than
 *  images. Well under a megabyte at a few thousand photos (D36). */
export interface SlideshowEntry {
  id: string;
  src: string;
  caption?: string;
  uploaderName: string;
}
